import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Search, RefreshCw, Eye, MoreVertical, CheckCircle, XCircle, DollarSign, Truck, User, Filter, EyeOff, Edit, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { adminAPI } from '@/services/api';
import DataTable from '@/components/ui/data-table';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useConstantsContext } from '@/contexts/ConstantsContext';
import { constantsUtils } from '@/hooks/useConstants';

const OperationsOffersList = ({ onStatisticsUpdate, constants: constantsProp }) => {
  const navigate = useNavigate();
  // Utiliser les constantes passées en prop, sinon utiliser le contexte
  const constantsContext = useConstantsContext();
  const constants = constantsProp || constantsContext?.constants;
  const [searchTerm, setSearchTerm] = useState('');
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [transporterFilter, setTransporterFilter] = useState('all');
  const [actionDialog, setActionDialog] = useState({ open: false, offer: null, action: null }); // 'accept' or 'reject'
  const [deleteDialog, setDeleteDialog] = useState({ open: false, offer: null });

  const isMountedRef = useRef(true);

  const loadOffers = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (transporterFilter !== 'all') params.transporter = transporterFilter;

      const response = await adminAPI.getAllOffers(params);
      if (!isMountedRef.current) return;

      const offersData = response.data.results || response.data || [];
      setOffers(offersData);

      if (onStatisticsUpdate && isMountedRef.current) {
        const stats = {
          total: offersData.length,
          pending: offersData.filter(o => o.status === 'PENDING').length,
          accepted: offersData.filter(o => o.status === 'ACCEPTED').length,
          rejected: offersData.filter(o => o.status === 'REJECTED').length,
        };
        onStatisticsUpdate(stats);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Erreur lors du chargement des offres:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les offres',
        variant: 'destructive',
      });
      setOffers([]);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, transporterFilter]);

  useEffect(() => {
    isMountedRef.current = true;
    loadOffers();

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, transporterFilter]);

  const handleAcceptOffer = async () => {
    if (!actionDialog.offer) return;

    try {
      const response = await adminAPI.acceptOffer(actionDialog.offer.id);
      toast({
        title: 'Succès',
        description: response.data?.detail || 'Offre acceptée avec succès',
      });
      setActionDialog({ open: false, offer: null, action: null });
      loadOffers();
    } catch (error) {
      console.error('Erreur lors de l\'acceptation:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible d\'accepter l\'offre',
        variant: 'destructive',
      });
    }
  };

  const handleRejectOffer = async () => {
    if (!actionDialog.offer) return;

    try {
      const response = await adminAPI.rejectOffer(actionDialog.offer.id);
      toast({
        title: 'Succès',
        description: response.data?.detail || 'Offre rejetée avec succès',
      });
      setActionDialog({ open: false, offer: null, action: null });
      loadOffers();
    } catch (error) {
      console.error('Erreur lors du rejet:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de rejeter l\'offre',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteOffer = async () => {
    if (!deleteDialog.offer) return;

    try {
      await adminAPI.deleteOffer(deleteDialog.offer.id);
      toast({
        title: 'Succès',
        description: 'Offre supprimée avec succès',
      });
      setDeleteDialog({ open: false, offer: null });
      loadOffers();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de supprimer l\'offre',
        variant: 'destructive',
      });
    }
  };


  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    // Afficher le nombre complet avec séparateurs de milliers
    return `${Number(amount).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} GNF`;
  };


  const getStatusBadge = (status) => {
    if (!constants) return <Badge>{status}</Badge>;
    const label = constantsUtils.getLabel(constants.offer_status, status);
    const statusConfig = {
      'PENDING': { color: 'bg-yellow-500', icon: Package },
      'ACCEPTED': { color: 'bg-green-500', icon: CheckCircle },
      'REJECTED': { color: 'bg-red-500', icon: XCircle },
      'EXPIRED': { color: 'bg-gray-500', icon: Package },
    };
    const config = statusConfig[status] || { color: 'bg-gray-500', icon: Package };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const filteredOffers = offers.filter(offer => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const request = offer.request_detail || offer.request;
    const transporter = offer.transporter_detail || offer.transporter;
    return (
      offer.id?.toLowerCase().includes(searchLower) ||
      request?.logiconnect_id?.toLowerCase().includes(searchLower) ||
      transporter?.organisation_name?.toLowerCase().includes(searchLower) ||
      transporter?.user?.email?.toLowerCase().includes(searchLower)
    );
  });

  const columns = [
    {
      id: 'id',
      header: 'ID',
      accessorKey: 'id',
      cell: ({ row }) => {
        const offer = row.original;
        return (
          <div className="text-sm font-mono text-muted-foreground">
            {offer.id?.substring(0, 8)}...
          </div>
        );
      },
    },
    {
      id: 'request',
      header: 'Demande',
      accessorKey: 'request',
      cell: ({ row }) => {
        const offer = row.original;
        const request = offer.request_detail || offer.request;
        return request?.logiconnect_id ? (
          <LogiConnectIdBadge id={request.logiconnect_id} size="sm" />
        ) : (
          <span className="text-muted-foreground text-sm">N/A</span>
        );
      },
    },
    {
      id: 'transporter',
      header: 'Transporteur',
      accessorKey: 'transporter',
      cell: ({ row }) => {
        const offer = row.original;
        const transporter = offer.transporter_detail || offer.transporter;
        return (
          <div className="text-sm">
            <p className="font-medium">{transporter?.organisation_name || transporter?.user?.first_name || 'N/A'}</p>
            <p className="text-xs text-muted-foreground">{transporter?.user?.email || ''}</p>
          </div>
        );
      },
    },
    {
      id: 'price',
      header: 'Prix',
      accessorKey: 'price',
      cell: ({ row }) => {
        const offer = row.original;
        return (
          <div className="text-sm">
            <span className="font-bold text-secondary">{formatCurrency(offer.price)}</span>
          </div>
        );
      },
    },
    {
      id: 'vehicle',
      header: 'Véhicule',
      accessorKey: 'vehicle',
      cell: ({ row }) => {
        const offer = row.original;
        const vehicle = offer.vehicle_detail || offer.vehicle;
        return (
          <div className="flex items-center gap-1 text-sm">
            <Truck className="w-4 h-4 text-muted-foreground" />
            <span>{vehicle?.plate_number || 'N/A'}</span>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Statut',
      accessorKey: 'status',
      cell: ({ row }) => {
        return getStatusBadge(row.original.status);
      },
    },
    {
      id: 'commission',
      header: 'Commission plateforme',
      cell: ({ row }) => {
        const offer = row.original;
        const request = offer.request_detail || offer.request;
        const offerPrice = parseFloat(offer.price) || 0;
        let commission = 0;

        if (request?.commission_type === 'PERCENTAGE' && request?.commission_percentage) {
          commission = offerPrice * (request.commission_percentage / 100);
        } else if (request?.commission_type === 'FIXED_AMOUNT' && request?.commission_fixed_amount) {
          commission = parseFloat(request.commission_fixed_amount);
        }

        return commission > 0 ? (
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-purple-400">
              {formatCurrency(commission)}
            </span>
            {request?.commission_type === 'PERCENTAGE' && (
              <span className="text-xs text-muted-foreground">
                {request.commission_percentage}%
              </span>
            )}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">Aucune</span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const offer = row.original;
        const canAccept = offer.status === 'PENDING';
        const canReject = offer.status === 'PENDING';

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                const request = offer.request_detail || offer.request;
                const transporter = offer.transporter_detail || offer.transporter;
                if (transporter?.id) {
                  navigate(`/admin/transporters/${transporter.id}/offers/${offer.id}`);
                }
              }}>
                <Eye className="w-4 h-4 mr-2" />
                Voir détails
              </DropdownMenuItem>
              {canAccept && (
                <DropdownMenuItem onClick={() => setActionDialog({ open: true, offer, action: 'accept' })}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Accepter
                </DropdownMenuItem>
              )}
              {canReject && (
                <DropdownMenuItem onClick={() => setActionDialog({ open: true, offer, action: 'reject' })}>
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeter
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => setDeleteDialog({ open: true, offer })}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {/* Filtres et recherche */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par ID, demande, transporteur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {constants?.offer_status?.map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={loadOffers} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Tableau */}
      <Card>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            data={filteredOffers}
            loading={loading}
            emptyMessage="Aucune offre trouvée"
          />
        </CardContent>
      </Card>

      {/* Dialog pour accepter/rejeter */}
      <AlertDialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionDialog.action === 'accept' ? 'Accepter l\'offre' : 'Rejeter l\'offre'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionDialog.action === 'accept'
                ? `Êtes-vous sûr de vouloir accepter cette offre ? Un booking sera créé automatiquement.`
                : `Êtes-vous sûr de vouloir rejeter cette offre ?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={actionDialog.action === 'accept' ? handleAcceptOffer : handleRejectOffer}
              className={actionDialog.action === 'accept' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {actionDialog.action === 'accept' ? 'Accepter' : 'Rejeter'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog pour supprimer l'offre */}
      {/* Dialog pour supprimer l'offre */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-destructive/10 rounded-full">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <AlertDialogTitle className="text-xl font-bold text-destructive">
                ⚠️ ATTENTION - SUPPRESSION DÉFINITIVE
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p className="font-semibold text-foreground">
                Vous êtes sur le point de supprimer définitivement cette offre.
              </p>
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
                <p className="font-medium text-destructive">
                  <strong>ID de l'offre :</strong> {deleteDialog.offer?.id?.slice(-8) || 'N/A'}
                </p>
                {deleteDialog.offer?.price && (
                  <p className="text-sm">
                    <strong>Prix :</strong> {formatCurrency(deleteDialog.offer.price)}
                  </p>
                )}
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400 mb-1">
                  ⚠️ Cette action est irréversible !
                </p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                  <li>L'offre sera supprimée de manière permanente</li>
                  <li>Toutes les données associées seront perdues</li>
                  <li>Cette action ne peut pas être annulée</li>
                </ul>
              </div>
              <p className="text-sm font-medium text-foreground">
                Êtes-vous absolument certain de vouloir continuer ?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOffer}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Oui, supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OperationsOffersList;
